import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/store/AppStore";
import Sidebar from "@/components/layout/Sidebar";
import TopBar from "@/components/layout/TopBar";
import FullLoader from "@/components/base/FullLoader";
import type { Role } from "@/types";

const AppShell = () => {
  const { data, loading, error } = useApp();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const style = data.settings.titleStyle;

  // Aplica el estilo de títulos editable (fuente, color, glow) a todo el ecosistema.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--title-font", `'${style.font}', system-ui, sans-serif`);
    root.style.setProperty("--title-color", style.color);
    root.style.setProperty(
      "--title-glow",
      style.glow ? "0 0 18px oklch(0.68 0.145 234 / 0.55)" : "none"
    );
  }, [style]);

  // Mantiene coherencia entre el rol activo y la sección visible.
  useEffect(() => {
    if (data.role === "admin" && location.pathname.startsWith("/asesor")) {
      navigate("/admin", { replace: true });
    } else if (data.role === "asesor" && location.pathname.startsWith("/admin")) {
      navigate("/asesor", { replace: true });
    }
  }, [data.role, location.pathname, navigate]);

  if (loading) return <FullLoader label="Cargando tus datos..." />;

  const handleRoleSwitch = (role: Role) => {
    navigate(role === "admin" ? "/admin" : "/asesor");
  };

  return (
    <div className="attd-app-bg min-h-screen">
      <Sidebar open={menuOpen} onClose={() => setMenuOpen(false)} />
      <div className="lg:pl-[262px] min-h-screen flex flex-col">
        <TopBar onOpenMenu={() => setMenuOpen(true)} onRoleSwitch={handleRoleSwitch} />
        <main className="flex-1 min-w-0 overflow-x-hidden px-4 md:px-6 py-6">
          {error && (
            <div className="mb-4 rounded-lg glass-panel border-primary-400/40 px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-primary-200" />
              <p className="text-xs text-foreground-800">{error}</p>
            </div>
          )}
          <Outlet />
        </main>
        <footer className="px-4 md:px-6 py-4 border-t border-accent-500/15">
          <p className="text-[11px] text-foreground-600 flex flex-wrap items-center gap-x-2">
            <span>© {new Date().getFullYear()} Amway Tienda Digital Domicilios · ATDD</span>
            <span className="text-foreground-500">· Datos protegidos en la nube</span>
            <span className="text-foreground-500">· Sesión de {data.role === "admin" ? "Administrador" : "Asesor"}</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default AppShell;